import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { AsyncConfirmationDialogComponent } from '../../../../core/components/async-confirmation-dialog/async-confirmation-dialog.component';
import { LoadingButtonComponent } from '../../../../core/components/buttons/loading/loading-button.component';
import { FormErrorsComponent } from '../../../../core/components/form-errors/form-errors.component';
import { InputFechaComponent } from '../../../../core/components/inputs/input-fecha/input-fecha.component';
import { InputTextareaComponent } from '../../../../core/components/inputs/input-textarea/input-textarea.component';
import { AG_GRID_DEFAULT_OPTIONS } from '../../../../core/config/aggrid.default.config';
import { BackendErrorService } from '../../../../core/services/backend-error/backend-error.service';
import { ToastService } from '../../../../core/services/toast/toast.service';
import { ReplaceEmptyStringsWithNull } from '../../../../core/utils/ReplaceEmptyStringsWithNull';
import { EmpresaFacturadoraService } from '../../../../maestros/services/maestros/empresa-facturadora/empresa-facturadora.service';
import { EmpresaFacturadora } from '../../../../models/maestros/empresa-facturadora.model';
import { AppRoutes } from '../../../../shared/route-paths.const';
import {
  FacturaVentaEfectivoDtoOut,
  VentaEfectivoDtoIn,
  VentaEfectivoForm,
} from '../../../models/venta-efectivo.model';
import { ContabilidadVentasService } from '../../../services/contabilidad-venta.service';

@Component({
  selector: 'app-venta-new',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    LoadingButtonComponent,
    InputFechaComponent,
    InputTextareaComponent,
    AgGridAngular,
    AsyncConfirmationDialogComponent,
    FormErrorsComponent,
  ],
  templateUrl: './venta-new.component.html',
  styleUrl: './venta-new.component.scss',
})
export class VentaNewComponent implements OnInit {
  gridApi!: GridApi;
  isColumnDropdownOpen = false;
  mostrarDialogoCrear = false;

  newVentaForm!: ReturnType<typeof this.fb.group<VentaEfectivoForm>>;
  isLoadingCrear = false;
  facturasParaVentaEnEfectivo: FacturaVentaEfectivoDtoOut[] = [];

  empresasRefacturadoras: EmpresaFacturadora[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly service: ContabilidadVentasService,
    private readonly toastService: ToastService,
    private readonly backendErrorService: BackendErrorService,
    private readonly empresaFacturadoraService: EmpresaFacturadoraService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.cargarTablasMaestras();
    this.setearSuscripciones();
    this.refreshGridColumns();
  }

  cargarTablasMaestras(): void {
    this.empresaFacturadoraService.setComboEmpresasFacturadoras(this.empresasRefacturadoras);

    this.newVentaForm.get('empresaFacturadoraId')?.setValue(null);
  }

  setearSuscripciones(): void {
    this.newVentaForm.get('empresaFacturadoraId')?.valueChanges.subscribe((value) => {
      //LLAMAR FACTURAS PARA SEPA
      if (value == null) {
        this.facturasParaVentaEnEfectivo = [];
      } else {
        this.cargarFacturasParaVentaEnEfectivo(value);
      }
    });
  }

  cargarFacturasParaVentaEnEfectivo(empresaFacturadoraId: number): void {
    this.service.getFacturasParaVentaEnEfectivo(empresaFacturadoraId).subscribe({
      next: (data: FacturaVentaEfectivoDtoOut[]) => {
        this.facturasParaVentaEnEfectivo = data;
      },
    });
  }

  crearVentaEfectivo(): void {
    this.isLoadingCrear = true;
    const rawVenta = this.newVentaForm.getRawValue();
    const ventaEfectivo: VentaEfectivoDtoIn = ReplaceEmptyStringsWithNull.clean(rawVenta);

    this.service.crearVentaEfectivo(ventaEfectivo).subscribe({
      next: (data) => {
        this.isLoadingCrear = false;
        this.toastService.success('Venta creada correctamente');
        this.router.navigate([`${AppRoutes.CONTABILIDAD_ONDA.VENTAS}/${data.id}`]);
      },
      error: (err) => {
        this.isLoadingCrear = false;
        this.handleCreateError(err);
      },
    });
  }

  private handleCreateError(err: any): void {
    if (err?.error?.errors) {
      this.backendErrorService.handleBackendErrors(err.error.errors, this.newVentaForm);
    } else {
      const errorMessage = err?.error?.message ?? 'Contacte con el administrador';
      this.toastService.error(`Error al crear la venta: ${errorMessage}`);
    }
  }

  initForm(): void {
    this.newVentaForm = this.fb.group<VentaEfectivoForm>({
      fechaAsientoContable: this.fb.control(null),
      empresaFacturadoraId: this.fb.control(null, { validators: Validators.required, nonNullable: true }),
      observaciones: this.fb.control(null),
      facturas: this.fb.control([], { validators: Validators.required, nonNullable: true }),
    });
  }

  gridOptions: GridOptions = {
    ...AG_GRID_DEFAULT_OPTIONS,
    domLayout: 'autoHeight',
    rowSelection: 'multiple',
    rowMultiSelectWithClick: true,
    onSelectionChanged: () => this.onSelectionChanged(),
    onGridReady: (params) => this.onGridReady(params),
  };

  columnDefs: ColDef[] = [];

  columnOptions = [
    { field: 'id', headerName: 'ID', selected: true },
    { field: 'codFactura', headerName: 'CodFactura', selected: true },
    { field: 'codContable', headerName: 'Codigo Contable', selected: true },
    { field: 'clienteNombre', headerName: 'Cliente', selected: true },
    { field: 'suministroDireccion', headerName: 'Suministro', selected: true },
    { field: 'baseImponible', headerName: 'Base Imponible', selected: true },
    { field: 'importeTotal', headerName: 'Importe Total', selected: true },
  ];

  onSelectionChanged(): void {
    const selectedNodes = this.gridApi?.getSelectedNodes() || [];
    const selectedIds = selectedNodes.map((node) => node.data.id);
    this.newVentaForm.controls.facturas.setValue(selectedIds);
  }

  onGridReady(params: any): void {
    this.gridApi = params.api;
  }

  toogleColumnDropdown(): void {
    this.isColumnDropdownOpen = !this.isColumnDropdownOpen;
  }

  toggleColumnaSeleccionada(option: any): void {
    option.selected = !option.selected;
    this.refreshGridColumns();
  }

  refreshGridColumns(): void {
    const id = {
      headerName: '',
      field: 'checkbox',
      width: 50,
      pinned: 'left' as const,
      checkboxSelection: true,
      headerCheckboxSelection: true,
    };

    this.columnDefs = [
      id,
      ...this.columnOptions.map((col) => ({
        field: col.field,
        headerName: col.headerName,
        hide: this.isColumnHide(col.field),
      })),
    ];
  }

  isColumnHide(columna: string): boolean {
    const column = this.columnOptions.find((col) => col.field === columna);
    return !column?.selected;
  }
}
